import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { useData } from "../context/DataContext";
import { PhoneCall, Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function AiCallDialog({ open, onClose, tenant }) {
  const { addCommunication } = useData();
  const [phase, setPhase] = useState("idle"); // idle | confirm | dialing | done | failed
  const [callRecord, setCallRecord] = useState(null);

  useEffect(() => {
    if (!open) {
      setPhase("idle");
      setCallRecord(null);
    }
  }, [open]);

  if (!tenant) return null;

  const startCall = async () => {
    setPhase("dialing");
    try {
      const c = await addCommunication({
        tenantId: tenant.id,
        type: "ai_call",
        lang: "en",
      });
      if (c && (c.status === "initiated" || c.status === "completed")) {
        setCallRecord(c);
        setPhase("done");
      } else if (c?.status === "skipped") {
        setCallRecord(c);
        setPhase("failed");
      } else {
        setCallRecord(c);
        setPhase("failed");
      }
    } catch (err) {
      console.error("AI call failed:", err);
      setPhase("failed");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="AI call reminder"
      description={`${tenant.name} · ${tenant.phone || "no phone"}`}
    >
      {phase === "idle" && (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 text-sm text-gray-700 dark:text-gray-300">
            The AI agent will call the tenant and remind them about their outstanding rent.
            A transcript will be available after the call ends.
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={() => setPhase("confirm")}>
              <PhoneCall className="h-4 w-4" />
              Start AI call
            </button>
          </div>
        </div>
      )}

      {phase === "confirm" && (
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
            <strong>Are you sure?</strong> The AI agent will call{" "}
            <strong>{tenant.name}</strong> at{" "}
            <strong>{tenant.phone || "no phone on file"}</strong>.
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setPhase("idle")}>
              Go back
            </button>
            <button className="btn-primary" onClick={startCall}>
              <PhoneCall className="h-4 w-4" />
              Confirm & call
            </button>
          </div>
        </div>
      )}

      {phase === "dialing" && (
        <div className="flex flex-col items-center gap-3 py-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">AI is calling {tenant.name}…</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">This may take a moment.</div>
        </div>
      )}

      {phase === "done" && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 p-4">
            <CheckCircle2 className="h-5 w-5 flex-none text-green-600 dark:text-green-400 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-green-800 dark:text-green-300">Call initiated</p>
              <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                The AI agent is calling {tenant.name}. You can view the transcript on the AI Calls page once the call ends.
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4">
            <XCircle className="h-5 w-5 flex-none text-red-600 dark:text-red-400 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                {callRecord?.status === "skipped" ? "Call skipped" : "Call failed"}
              </p>
              <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                {callRecord?.status === "skipped"
                  ? `${tenant.phone} is not a Safaricom number. AI calls are only available for Safaricom numbers.`
                  : `Could not call ${tenant.phone || tenant.name}. Please check the phone number and try again.`}
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
